import React from "react";
import {
  POSITION_HEADER_SUFFIX,
  POSITION_HEADER_TOOLTIP,
  getPositionDisplay,
} from "../../utils/positionFormatter";

const getInitials = (name) => {
  return name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

const ImageOrInitials = ({ src, alt, size = "small" }) => {
  const sizeClasses = {
    small: "w-6 h-6",
    medium: "w-8 h-8",
    large: "w-10 h-10",
  };

  if (src) {
    return (
      <img
        src={src}
        alt={alt}
        className={`${sizeClasses[size]} rounded-full`}
      />
    );
  }

  return (
    <div
      className={`${sizeClasses[size]} rounded-full bg-gray-100 flex items-center justify-center text-secondary font-medium`}
    >
      {getInitials(alt)}
    </div>
  );
};

const AgencyAgentsActivityTable = ({
  agencyPitches,
  agencyLoading,
  onPlayerClick,
  onClubClick,
}) => {
  return (
    <div className="bg-white rounded-lg p-7 border border-[#D6DBEA]">
      <h2 className="text-lg text-[#222A35] font-bold mb-7">
        Your agents activity
      </h2>
      <div className="overflow-x-auto">
        <table className="w-full border border-[#D6DBEA] rounded-lg table-sticky-first">
          <thead className="text-left text-sm text-secondary border-b bg-[#F7F7F7]">
            <tr>
              <th className="p-4 text-[#222A35] text-xs sm:text-sm font-medium whitespace-nowrap">
                AGENT
              </th>
              <th className="p-4 text-[#222A35] text-xs sm:text-sm font-medium whitespace-nowrap">
                PITCHED PLAYER
              </th>
              <th className="p-4 text-[#222A35] text-xs sm:text-sm font-medium whitespace-nowrap">
                TO CLUB
              </th>
              <th className="p-4 text-[#222A35] text-xs sm:text-sm font-medium whitespace-nowrap">
                <span
                  className="inline-flex items-center gap-1 normal-case"
                  title={POSITION_HEADER_TOOLTIP}
                >
                  <span>Position</span>
                  <span className="text-[10px] sm:text-xs text-[#667085] font-normal">
                    {POSITION_HEADER_SUFFIX}
                  </span>
                </span>
              </th>
              <th className="p-4 text-[#222A35] text-xs sm:text-sm font-medium whitespace-nowrap">
                STATUS
              </th>
              <th className="p-4 text-[#222A35] text-xs sm:text-sm font-medium whitespace-nowrap">
                DATE SENT
              </th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {agencyLoading ? (
              [...Array(3)].map((_, index) => (
                <tr key={`loading-${index}`} className="animate-pulse">
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                      <div className="h-5 bg-gray-200 rounded w-24"></div>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                      <div className="h-5 bg-gray-200 rounded w-24"></div>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-gray-200 rounded-full"></div>
                      <div className="h-5 bg-gray-200 rounded w-24"></div>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="h-5 bg-gray-200 rounded w-20"></div>
                  </td>
                  <td className="p-4">
                    <div className="h-5 bg-gray-200 rounded w-16"></div>
                  </td>
                  <td className="p-4">
                    <div className="h-5 bg-gray-200 rounded w-24"></div>
                  </td>
                </tr>
              ))
            ) : agencyPitches && agencyPitches.length > 0 ? (
              agencyPitches.map((pitch, index) => (
                <tr key={pitch.id || index}>
                  <td className="p-4 whitespace-nowrap">
                    <div className="flex items-center gap-2 flex-col sm:flex-row">
                      <ImageOrInitials
                        src={pitch.agent?.image}
                        alt={pitch.agent?.name || "Agent"}
                        size="medium"
                      />
                      <span className="font-bold text-[#222A35] text-xs sm:text-base">
                        {pitch.agent?.name || "Agent"}
                      </span>
                    </div>
                  </td>
                  <td className="p-4 whitespace-nowrap">
                    <div
                      onClick={() => onPlayerClick(pitch?.player?.id)}
                      className="cursor-pointer flex items-center gap-2"
                    >
                      <ImageOrInitials
                        src={pitch.player?.image}
                        alt={pitch.player?.name || "Player"}
                      />
                      <span
                        title={pitch.player?.name || "Player"}
                        className="font-bold text-[#222A35] text-xs sm:text-base block truncate max-w-[160px]"
                      >
                        {pitch.player?.name || "Player"}
                      </span>
                      {pitch.player?.isActive && (
                        <div className="w-2 h-2 rounded-full bg-blue-400"></div>
                      )}
                    </div>
                  </td>
                  <td className="p-4 whitespace-nowrap">
                    <div
                      onClick={() => onClubClick(pitch?.club?.id)}
                      className="cursor-pointer flex items-center gap-2"
                    >
                      <ImageOrInitials
                        src={pitch.club?.image}
                        alt={pitch.club?.name || "Club"}
                      />
                      <span className="text-[#222A35] text-xs sm:text-base">
                        {pitch.club?.name || "Club"}
                      </span>
                    </div>
                  </td>
                  <td className="p-4 text-[#222A35] text-xs sm:text-base whitespace-nowrap">
                    {getPositionDisplay(pitch.player, pitch.position || "N/A")}
                  </td>
                  <td className="p-4 whitespace-nowrap">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        pitch.status === "accepted"
                          ? "bg-[#EBF9EE] text-[#42936D]"
                          : pitch.status === "rejected"
                          ? "bg-[#FFEAEE] text-[#FF2D55]"
                          : "bg-[#F7F0E6] text-[#BE6F01]"
                      }`}
                    >
                      {pitch.status?.charAt(0).toUpperCase() +
                        pitch.status?.slice(1).replace("_", " ") || "N/A"}
                    </span>
                  </td>
                  <td className="p-4 text-[#222A35] text-xs sm:text-base whitespace-nowrap">
                    {pitch.dateSent
                      ? new Date(pitch.dateSent).toLocaleDateString()
                      : "N/A"}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="p-4 text-center text-gray-500">
                  No agent activities found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AgencyAgentsActivityTable;
